/**
 * ShoppingList Store
 *
 * Manages shopping list state using Zustand with persistence.
 * Provides operations for generating shopping lists from meal plans,
 * managing shopping items, and supporting offline access.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  ShoppingList,
  ShoppingItem,
  ShoppingListGenerateParams,
  ShoppingItemUpdateData,
  CategoryGroup,
} from '../types/shoppingList';
import api from '../services/api';

interface ShoppingListState {
  shoppingLists: ShoppingList[];
  currentShoppingList: ShoppingList | null;
  isLoading: boolean;
  error: string | null;
  isOnline: boolean;

  // Actions
  fetchShoppingLists: () => Promise<void>;
  fetchShoppingList: (id: number) => Promise<void>;
  generateShoppingList: (params: ShoppingListGenerateParams) => Promise<ShoppingList>;
  deleteShoppingList: (id: number) => Promise<void>;
  setCurrentShoppingList: (shoppingList: ShoppingList | null) => void;

  // Shopping Item Actions
  updateShoppingItem: (
    shoppingListId: number,
    itemId: number,
    data: ShoppingItemUpdateData
  ) => Promise<ShoppingItem>;
  toggleItemChecked: (shoppingListId: number, itemId: number) => Promise<void>;

  // Utilities
  getItemsByCategory: () => CategoryGroup[];
  clearError: () => void;
  setOnlineStatus: (isOnline: boolean) => void;
}

/**
 * ShoppingList Store with Zustand
 *
 * Uses persist middleware to cache shopping lists for offline access.
 * Provides operations for generating lists from meal plans and managing items.
 */
export const useShoppingListStore = create<ShoppingListState>()(
  persist(
    (set, get) => ({
      shoppingLists: [],
      currentShoppingList: null,
      isLoading: false,
      error: null,
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,

      fetchShoppingLists: async () => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.get('/shopping_lists');

          set({
            shoppingLists: response.data.shopping_lists || response.data.data || response.data,
            isLoading: false,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch shopping lists',
          });
          throw error;
        }
      },

      fetchShoppingList: async (id: number) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.get(`/shopping_lists/${id}`);

          set({
            currentShoppingList: response.data.shopping_list || response.data.data || response.data,
            isLoading: false,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch shopping list',
          });
          throw error;
        }
      },

      generateShoppingList: async (params: ShoppingListGenerateParams) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.post('/shopping_lists/generate', params);
          const newShoppingList = response.data.shopping_list || response.data.data || response.data;

          set((state) => ({
            shoppingLists: [...state.shoppingLists, newShoppingList],
            currentShoppingList: newShoppingList,
            isLoading: false,
          }));

          return newShoppingList;
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to generate shopping list',
          });
          throw error;
        }
      },

      deleteShoppingList: async (id: number) => {
        set({ isLoading: true, error: null });

        try {
          await api.delete(`/shopping_lists/${id}`);

          set((state) => ({
            shoppingLists: state.shoppingLists.filter((list) => list.id !== id),
            currentShoppingList: state.currentShoppingList?.id === id ? null : state.currentShoppingList,
            isLoading: false,
          }));
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to delete shopping list',
          });
          throw error;
        }
      },

      setCurrentShoppingList: (shoppingList: ShoppingList | null) => {
        set({ currentShoppingList: shoppingList });
      },

      updateShoppingItem: async (
        shoppingListId: number,
        itemId: number,
        data: ShoppingItemUpdateData
      ) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.put(
            `/shopping_lists/${shoppingListId}/shopping_items/${itemId}`,
            { shopping_item: data }
          );
          const updatedItem = response.data.shopping_item || response.data.data || response.data;

          // Update the shopping item in the current shopping list
          set((state) => {
            if (!state.currentShoppingList || state.currentShoppingList.id !== shoppingListId) {
              return { isLoading: false };
            }

            const updatedItems = state.currentShoppingList.shopping_items?.map((item) =>
              item.id === itemId ? updatedItem : item
            );

            return {
              currentShoppingList: {
                ...state.currentShoppingList,
                shopping_items: updatedItems,
              },
              isLoading: false,
            };
          });

          return updatedItem;
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to update shopping item',
          });
          throw error;
        }
      },

      toggleItemChecked: async (shoppingListId: number, itemId: number) => {
        const { currentShoppingList, isOnline } = get();
        if (!currentShoppingList || currentShoppingList.id !== shoppingListId) return;

        const item = currentShoppingList.shopping_items?.find((item) => item.id === itemId);
        if (!item) return;

        const newCheckedState = !item.checked;

        // Optimistic update: Update local state immediately
        set((state) => {
          if (!state.currentShoppingList || state.currentShoppingList.id !== shoppingListId) {
            return state;
          }

          const updatedItems = state.currentShoppingList.shopping_items?.map((item) =>
            item.id === itemId ? { ...item, checked: newCheckedState } : item
          );

          return {
            currentShoppingList: {
              ...state.currentShoppingList,
              shopping_items: updatedItems,
            },
          };
        });

        // If online, sync with backend
        if (isOnline) {
          try {
            await api.put(
              `/shopping_lists/${shoppingListId}/shopping_items/${itemId}`,
              { shopping_item: { checked: newCheckedState } }
            );
          } catch (error) {
            // Revert optimistic update on error
            set((state) => {
              if (!state.currentShoppingList || state.currentShoppingList.id !== shoppingListId) {
                return state;
              }

              const revertedItems = state.currentShoppingList.shopping_items?.map((item) =>
                item.id === itemId ? { ...item, checked: !newCheckedState } : item
              );

              return {
                currentShoppingList: {
                  ...state.currentShoppingList,
                  shopping_items: revertedItems,
                },
                error: error instanceof Error ? error.message : 'Failed to update item',
              };
            });
            throw error;
          }
        }
        // If offline, the optimistic update persists via localStorage
      },

      getItemsByCategory: () => {
        const { currentShoppingList } = get();
        if (!currentShoppingList?.shopping_items) return [];

        const categoryMap = new Map<string, ShoppingItem[]>();

        currentShoppingList.shopping_items.forEach((item) => {
          const category = item.category || 'Uncategorized';
          if (!categoryMap.has(category)) {
            categoryMap.set(category, []);
          }
          categoryMap.get(category)!.push(item);
        });

        return Array.from(categoryMap.entries()).map(([category, items]) => ({
          category,
          items,
        }));
      },

      clearError: () => {
        set({ error: null });
      },

      setOnlineStatus: (isOnline: boolean) => {
        set({ isOnline });
      },
    }),
    {
      name: 'shopping-list-storage', // localStorage key
      version: 2, // Bump to invalidate stale cached data from old format
      partialize: (state) => ({
        // Persist shopping lists and currentShoppingList for offline access
        // Don't persist loading/error/online states
        shoppingLists: state.shoppingLists,
        currentShoppingList: state.currentShoppingList,
      }),
    }
  )
);

// Set up online/offline event listeners
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    useShoppingListStore.getState().setOnlineStatus(true);
  });

  window.addEventListener('offline', () => {
    useShoppingListStore.getState().setOnlineStatus(false);
  });
}
